import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Typography from '@mui/material/Typography';

import imgLogo from "../assets/bielenda_logo.png";
import larcLogo from "../assets/l'arc-logo.png";

const ResponsiveAppBar: React.FC = () => {
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [skinDropdownOpen, setSkinDropdownOpen] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setSkinDropdownOpen(false);
            }
        };

        if (skinDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [skinDropdownOpen]);

    const toggleDrawer = (open: boolean) => () => {
        setDrawerOpen(open);
    };

    return (
        <>
            <AppBar
                position="absolute"
                sx={{
                    backgroundColor: '#fff',
                    boxShadow: "none",
                    zIndex: 99,
                    padding: "10px 0"
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar
                        disableGutters
                        sx={{ justifyContent: "space-between" }}
                    >
                        {/* Logo */}
                        <Link href="/#home" sx={{ display: { xs: "none", md: "flex" }, alignItems: "flex-start", gap: "20px" }}>
                            {/* <Box
                                component="img"
                                src={larcLogo}
                                alt="L'A logo"
                                loading="lazy"
                                sx={{
                                    objectFit: "contain",
                                    height: "60px"
                                }}
                            /> */}
                            <Box
                                component="img"
                                src={imgLogo}
                                alt="Bielenda logo"
                                loading="lazy"
                                sx={{
                                    objectFit: "contain",
                                    height: "60px"
                                }}
                            />
                        </Link>

                        {/* Desktop Links */}
                        <Box
                            sx={{
                                display: { xs: "none", md: "flex" },
                                alignItems: "center",
                                gap: "40px",
                            }}
                        >
                            <Link
                                href="/"
                                color="rgba(61, 61, 61, 1)"
                                sx={{
                                    textDecoration: "none",
                                    transition: "color 0.3s",
                                    "&:hover": { color: "#002e8c" },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Quicksand, sans-serif',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        lineHeight: '20px',
                                        letterSpacing: '0.1em',
                                        textAlign: 'center',
                                    }}
                                >
                                    HOME
                                </Typography>
                            </Link>
                            <Link
                                href="/story"
                                color="rgba(61, 61, 61, 1)"
                                sx={{
                                    textDecoration: "none",
                                    transition: "color 0.3s",
                                    "&:hover": { color: "#002e8c" },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Quicksand, sans-serif',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        lineHeight: '20px',
                                        letterSpacing: '0.1em',
                                        textAlign: 'center',
                                    }}
                                >
                                    STORY
                                </Typography>
                            </Link>
                            <Box
                                ref={dropdownRef}
                                sx={{
                                    position: 'relative',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Quicksand, sans-serif',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        lineHeight: '20px',
                                        letterSpacing: '0.1em',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        color: 'rgba(61, 61, 61, 1)',
                                        transition: "color 0.3s",
                                        "&:hover": { color: "#002e8c" },
                                    }}
                                    onClick={() => window.location.href = '/skin/bielenda'}
                                >
                                    SKIN
                                </Typography>

                                {/* Dropdown Menu */}
                                {skinDropdownOpen && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            backgroundColor: '#fff',
                                            borderRadius: '20px',
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                                            padding: '15px 0',
                                            minWidth: '240px',
                                            zIndex: 1000,
                                            border: '1px solid rgba(221, 221, 221, 1)',
                                            animation: 'fadeInDown 0.3s ease',
                                            '@keyframes fadeInDown': {
                                                '0%': {
                                                    opacity: 0,
                                                    transform: 'translateX(-50%) translateY(-10px)',
                                                },
                                                '100%': {
                                                    opacity: 1,
                                                    transform: 'translateX(-50%) translateY(0)',
                                                },
                                            },
                                        }}
                                    >
                                        <Box
                                            onClick={() => {
                                                window.location.href = '/skin/larc';
                                                setSkinDropdownOpen(false);
                                            }}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '12px 20px',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(138, 43, 226, 0.05)',
                                                },
                                                transition: 'background-color 0.2s ease',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={larcLogo}
                                                alt="L'A"
                                                sx={{
                                                    height: '35px',
                                                    marginRight: '12px',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                            <Box sx={{ flex: 1, textAlign: 'right' }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Quicksand',
                                                        fontSize: '16px',
                                                        fontWeight: 600,
                                                        color: 'rgba(61, 61, 61, 1)',
                                                        lineHeight: '1.2',
                                                    }}
                                                >
                                                    L'A
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Quicksand',
                                                        fontSize: '12px',
                                                        color: 'rgba(136, 136, 136, 1)',
                                                    }}
                                                >
                                                    Premium Solutions
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                height: '1px',
                                                backgroundColor: 'rgba(221, 221, 221, 1)',
                                                margin: '5px 0',
                                            }}
                                        />

                                        <Box
                                            onClick={() => {
                                                window.location.href = '/skin/bielenda';
                                                setSkinDropdownOpen(false);
                                            }}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '12px 20px',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 48, 135, 0.05)',
                                                },
                                                transition: 'background-color 0.2s ease',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={imgLogo}
                                                alt="Bielenda"
                                                sx={{
                                                    height: '30px',
                                                    marginRight: '12px',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                            <Box sx={{ flex: 1, textAlign: 'right' }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Quicksand',
                                                        fontSize: '16px',
                                                        fontWeight: 600,
                                                        color: 'rgba(61, 61, 61, 1)',
                                                        lineHeight: '1.2',
                                                    }}
                                                >
                                                    Bielenda
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Quicksand',
                                                        fontSize: '12px',
                                                        color: 'rgba(136, 136, 136, 1)',
                                                    }}
                                                >
                                                    Professional Skincare
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                            {/* <Link
                                href="/skin"
                                color="rgba(61, 61, 61, 1)"
                                sx={{
                                    textDecoration: "none",
                                    transition: "color 0.3s",
                                    "&:hover": { color: "#002e8c" },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Quicksand, sans-serif',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        lineHeight: '20px',
                                        letterSpacing: '0.1em',
                                        textAlign: 'center',
                                    }}
                                >
                                    PRODUCTS
                                </Typography>
                            </Link> */}
                            <Link
                                href="https://lavishamsia.com/"
                                color="rgba(61, 61, 61, 1)"
                                sx={{
                                    textDecoration: "none",
                                    transition: "color 0.3s",
                                    "&:hover": { color: "#002e8c" },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Quicksand, sans-serif',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        lineHeight: '20px',
                                        letterSpacing: '0.1em',
                                        textAlign: 'center',
                                    }}
                                >
                                    LAVISHA
                                </Typography>
                            </Link>
                        </Box>

                        {/* Login Button */}
                        <Box
                            sx={{
                                display: { xs: "none", md: "flex" },
                                alignItems: "center",
                                gap: "20px",
                            }}
                        >
                            <Link
                                href="/story"
                                color="rgba(61, 61, 61, 1)"
                                sx={{
                                    textDecoration: "none",
                                    transition: "color 0.3s",
                                    "&:hover": { color: "#002e8c" },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Quicksand, sans-serif',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        lineHeight: '20px',
                                        letterSpacing: '0.1em',
                                        textAlign: 'center',
                                    }}
                                >
                                    LOGIN
                                </Typography>
                            </Link>
                        </Box>

                        {/* Mobile Drawer */}
                        <Box sx={{ display: { md: "none" } }}>
                            <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="menu"
                                onClick={toggleDrawer(true)}
                                sx={{ fontSize: '50px', color: '#002e8c' }} // Adjust fontSize to make it thicker
                            >
                                <MenuIcon sx={{ fontSize: '30px' }} />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
            >
                <Box
                    sx={{ width: 280, padding: "20px" }}
                    role="presentation"
                    onClick={toggleDrawer(false)}
                    onKeyDown={toggleDrawer(false)}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
                        {/* <Box
                            component="img"
                            src={larcLogo}
                            alt="L'A logo"
                            loading="lazy"
                            sx={{
                                objectFit: "contain",
                                height: { xs: "50px", md: "80px" }
                            }}
                        /> */}
                        <Box
                            component="img"
                            src={imgLogo}
                            alt="Bielenda logo"
                            loading="lazy"
                            sx={{
                                objectFit: "contain",
                                height: { xs: "50px", md: "80px" }
                            }}
                        />
                    </Box>
                    <Divider />
                    <List>
                        <ListItem button component="a" href="/#home">
                            <ListItemText
                                primary={
                                    <Typography
                                        sx={{
                                            fontFamily: 'Quicksand, sans-serif',
                                            fontSize: '16px',
                                            fontWeight: 500,
                                            lineHeight: '20px',
                                            letterSpacing: '0.1em',
                                            textAlign: 'center',
                                            color: 'rgba(61, 61, 61, 1)',
                                            transition: "color 0.3s",
                                            "&:hover": { color: "#002e8c" },
                                        }}
                                    >
                                        Home
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem button component="a" href="/story">
                            <ListItemText
                                primary={
                                    <Typography
                                        sx={{
                                            fontFamily: 'Quicksand, sans-serif',
                                            fontSize: '16px',
                                            fontWeight: 500,
                                            lineHeight: '20px',
                                            letterSpacing: '0.1em',
                                            textAlign: 'center',
                                            color: 'rgba(61, 61, 61, 1)',
                                            transition: "color 0.3s",
                                            "&:hover": { color: "#002e8c" },
                                        }}
                                    >
                                        Story
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem button component="a" href="/skin/bielenda">
                            <ListItemText
                                primary={
                                    <Typography
                                        sx={{
                                            fontFamily: 'Quicksand, sans-serif',
                                            fontSize: '16px',
                                            fontWeight: 500,
                                            lineHeight: '20px',
                                            letterSpacing: '0.1em',
                                            textAlign: 'center',
                                            color: 'rgba(61, 61, 61, 1)',
                                            transition: "color 0.3s",
                                            "&:hover": { color: "#002e8c" },
                                        }}
                                    >
                                        Skin
                                    </Typography>
                                }
                            />
                        </ListItem>
                        {/* <ListItem button component="a" href="/skin/larc">
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <Box
                                            component="img"
                                            src={larcLogo}
                                            alt="L'A"
                                            sx={{
                                                height: '35px',
                                                objectFit: 'contain',
                                            }}
                                        />
                                        <Box sx={{ flex: 1, textAlign: 'right' }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Quicksand, sans-serif',
                                                    fontSize: '16px',
                                                    fontWeight: 600,
                                                    lineHeight: '1.2',
                                                    color: 'rgba(61, 61, 61, 1)',
                                                }}
                                            >
                                                L'A
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Quicksand, sans-serif',
                                                    fontSize: '12px',
                                                    color: 'rgba(136, 136, 136, 1)',
                                                }}
                                            >
                                                Premium Solutions
                                            </Typography>
                                        </Box>
                                    </Box>
                                }
                            />
                        </ListItem>
                        <ListItem button component="a" href="/skin/bielenda">
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <Box
                                            component="img"
                                            src={imgLogo}
                                            alt="Bielenda"
                                            sx={{
                                                height: '30px',
                                                objectFit: 'contain',
                                            }}
                                        />
                                        <Box sx={{ flex: 1, textAlign: 'right' }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Quicksand, sans-serif',
                                                    fontSize: '16px',
                                                    fontWeight: 600,
                                                    lineHeight: '1.2',
                                                    color: 'rgba(61, 61, 61, 1)',
                                                }}
                                            >
                                                Bielenda
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Quicksand, sans-serif',
                                                    fontSize: '12px',
                                                    color: 'rgba(136, 136, 136, 1)',
                                                }}
                                            >
                                                Professional Skincare
                                            </Typography>
                                        </Box>
                                    </Box>
                                }
                            />
                        </ListItem> */}
                        <ListItem button component="a" href="https://lavishamsia.com/">
                            <ListItemText
                                primary={
                                    <Typography
                                        sx={{
                                            fontFamily: 'Quicksand, sans-serif',
                                            fontSize: '16px',
                                            fontWeight: 500,
                                            lineHeight: '20px',
                                            letterSpacing: '0.1em',
                                            textAlign: 'center',
                                            color: 'rgba(61, 61, 61, 1)',
                                            transition: "color 0.3s",
                                            "&:hover": { color: "#002e8c" },
                                        }}
                                    >
                                        Lavisha
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>

                    <Divider />

                    {/* Add Login Button */}
                    <ListItem button component="a" href="https://member.lbbmalaysia.com/#/login">
                        <ListItemText
                            primary={
                                <Typography
                                    sx={{
                                        fontFamily: 'Quicksand, sans-serif',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        lineHeight: '20px',
                                        letterSpacing: '0.1em',
                                        textAlign: 'center',
                                        color: 'rgba(61, 61, 61, 1)',
                                        transition: "color 0.3s",
                                        "&:hover": { color: "#002e8c" },
                                    }}
                                >
                                    Login
                                </Typography>
                            }
                        />
                    </ListItem>
                </Box>
            </Drawer>
        </>
    );
};

export default ResponsiveAppBar;
